/**
 * Utility functions for NavWidget to enable isolated unit testing of click logic
 * without requiring full DOM rendering of the third-party Navigation component.
 *
 * @author Copilot
 */

/**
 * Classifies a URL as external, same-domain, or relative/fragment.
 * SSR-safe: `window` may be undefined in server-side rendering contexts.
 * Malformed URLs are treated as internal (relative) links.
 *
 * @param url - The URL string to classify.
 * @returns {{ isExternal: boolean; isSameDomain: boolean; relativePath?: string }}
 *   - isExternal: true when the URL points to a different origin.
 *   - isSameDomain: true when the absolute URL shares the current page origin.
 *   - relativePath: the path+search+hash string when isSameDomain is true.
 * @throws Never – all errors are caught internally and treated as internal links.
 */
export function getLinkType(
  url: string
): {
  isExternal: boolean;
  isSameDomain: boolean;
  relativePath?: string;
} {
  if (!url || typeof url !== 'string') {
    return { isExternal: false, isSameDomain: false };
  }
  try {
    // SSR-safe: window may be undefined
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const parsedUrl = new URL(url, origin || 'http://localhost');
    if (/^https?:\/\//i.test(url)) {
      if (origin && parsedUrl.origin === origin) {
        return {
          isExternal: false,
          isSameDomain: true,
          relativePath:
            parsedUrl.pathname + parsedUrl.search + parsedUrl.hash || '/',
        };
      }
      return { isExternal: true, isSameDomain: false };
    }
    // Relative or fragment URLs
    return { isExternal: false, isSameDomain: false };
  } catch {
    // Malformed URL — treat as an internal/relative link
    return { isExternal: false, isSameDomain: false };
  }
}

export interface NavWidgetClickParams {
  editing: boolean;
  style?: string; // 'Horizontal' | 'Vertical'
  menuItems: Array<any>;
  clickedLabel: string;
  trackNavWidget: (action: string, payload: Record<string, any>) => void;
  onChange?: (
    newConfig: any,
    widgetId: string,
    shouldPersist?: boolean
  ) => void;
  widgetState?: { id: string; config: any };
  visibleChange?: (currentMenuItem: number) => void;
}

/**
 * Handles a navigation item click applying tracking + editing side-effects.
 * Returns metadata describing what occurred for test assertions.
 *
 * @param params NavWidgetClickParams
 * @returns { tracked: boolean; navItem?: any; currentMenuItemApplied?: number }
 */
export function navWidgetHandleClick(
  params: NavWidgetClickParams
): { tracked: boolean; navItem?: any; currentMenuItemApplied?: number } {
  const {
    editing,
    style,
    menuItems,
    clickedLabel,
    trackNavWidget,
    onChange,
    widgetState,
    visibleChange,
  } = params;

  const navItem = menuItems.find(mi => mi.displayText === clickedLabel);
  let tracked = false;
  let currentMenuItemApplied: number | undefined;

  if (!editing && navItem) {
    trackNavWidget('nav_widget_click', {
      navItemId: navItem.id,
      navItemLabel: navItem.analyticsLabel || navItem.displayText,
      destinationUrl: navItem.url || null,
      destinationPageId: navItem.pageSelected?.id || null,
      isSection: !!navItem.isSection,
    });
    tracked = true;
  }

  if ((editing || style === 'Vertical') && navItem && widgetState?.config) {
    currentMenuItemApplied = navItem.id;
    onChange?.(
      { ...widgetState.config, currentMenuItem: navItem.id },
      widgetState.id,
      false
    );
    visibleChange?.(navItem.id);
  }

  return { tracked, navItem, currentMenuItemApplied };
}
